import {
  GIFT_SEARCH_AGE_GROUPS,
  type GiftSearchAgeGroup,
  type GiftSearchSort,
} from '../schemas/endpoints/theaWebSearchGifts';

/** Topic sorts and catalogue age labels. Both sides use the generated age
 * vocabulary; the backend applies selected bands as an OR over the index. */

/**
 * The board's three orderings, in the order they listed them.
 *
 * `trending` IS `relevance` on the wire. They are the same server sort and the
 * rename is not cosmetic: on a topic hub the query is the topic ("fall"), so
 * relevance-ordering a topic query is exactly "the most on-topic items first",
 * which is what a shopper reads as trending. Calling it `relevance` in the UI
 * would be describing our retrieval to someone who is shopping.
 *
 * WHAT IT IS NOT: a popularity ranking. Nothing in `products` carries sales or
 * view counts, so there is no genuine trending signal to sort by, and the
 * curated `theaWebHubTrending/current` row is a hand-picked list of twelve
 * rather than an ordering over the catalogue. This is the honest best available
 * and it should not be described to the board as measured popularity.
 */
export const HUB_TOPIC_SORTS = ['trending', 'price_desc', 'price_asc'] as const;

export type HubTopicSort = (typeof HUB_TOPIC_SORTS)[number];

export const HUB_TOPIC_SORT_LABELS: Record<HubTopicSort, string> = {
  trending: 'Trending',
  price_desc: 'Price: high to low',
  price_asc: 'Price: low to high',
};

/** The default, and what an unrecognised value falls back to. */
export const HUB_TOPIC_DEFAULT_SORT: HubTopicSort = 'trending';

/** UI sort -> wire sort. See `HUB_TOPIC_SORTS` on why trending maps to relevance. */
export const hubTopicSortToWire = (sort: HubTopicSort): GiftSearchSort =>
  sort === 'trending' ? 'relevance' : sort;

export interface HubAgeFilter {
  value: GiftSearchAgeGroup;
  /** Compact label, retaining numbers to disambiguate adjacent age bands. */
  label: string;
}

const AGE_LABELS: Record<GiftSearchAgeGroup, string> = {
  NEWBORN_0_2_MONTHS: 'Newborn (0–2 mo)',
  YOUNG_INFANT_2_6_MONTHS: 'Baby (2–6 mo)',
  OLDER_INFANT_6_12_MONTHS: 'Baby (6–12 mo)',
  TODDLER_1_3_YEARS: 'Toddler (1–3)',
  PRESCHOOL_3_5_YEARS: 'Preschool (3–5)',
  ELEMENTARY_6_10_YEARS: 'Kids (6–10)',
  MIDDLE_SCHOOL_11_14_YEARS: 'Tween (11–14)',
  HIGH_SCHOOL_14_18_YEARS: 'Teen (14–18)',
  YOUNG_ADULT: 'Young adult',
  OLDER_ADULT: 'Adult',
};

// Generated order drives the UI, URL and request, never click order.
export const HUB_AGE_FILTERS: readonly HubAgeFilter[] = GIFT_SEARCH_AGE_GROUPS.map(
  (value) => ({ value, label: AGE_LABELS[value] }),
);

export const normalizeHubAges = (values: readonly string[]): GiftSearchAgeGroup[] =>
  GIFT_SEARCH_AGE_GROUPS.filter((band) => values.includes(band));

/** Permanent URL contract: repeated age=BAND, youngest first. A legacy single
 * age=BAND is already a one-element list. Unknown elements are dropped alone. */
export const readHubAges = (params: URLSearchParams): GiftSearchAgeGroup[] =>
  normalizeHubAges(params.getAll('age'));

export const writeHubAges = (params: URLSearchParams, values: readonly string[]): URLSearchParams => {
  const next = new URLSearchParams(params);
  next.delete('age');
  normalizeHubAges(values).forEach((band) => next.append('age', band));
  return next;
};

export const isHubTopicSort = (value: unknown): value is HubTopicSort =>
  HUB_TOPIC_SORTS.includes(value as HubTopicSort);

export const isHubAgeGroup = (value: unknown): value is GiftSearchAgeGroup =>
  GIFT_SEARCH_AGE_GROUPS.includes(value as GiftSearchAgeGroup);
